// deps
import React, { useCallback, useMemo, useRef } from "react";

// internal-deps
import { ElementObserver } from "@core-ui/components/ElementObserver";
import {
    ActionType,
    CouponCard as GenericCouponCardComponent,
} from "@swiggy-private/dls-web-recipe-common-web/dist/components/CouponCard";

import { Container } from "./styles";
import { COUPON_CARD_TESTS_IDS } from "@core-ui/constants/Coupon";
import {
    isGreatDealCouponGroup,
    isBestCouponGroup,
    isDoubleOfferCouponGroup,
    isPaymentOfferCouponGroup,
    getCouponType,
    getCouponListingType,
    isAppliedCouponGroup,
} from "@core-ui/helpers/Coupon";
import { isIPLSwiggySixesOfferCardWithAsset } from "@core-ui/helpers/Coupon";

//interfaces
import { COUPON_GROUP_TYPES, CouponInfo } from "@core-ui/interfaces/Common";
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";

// helpers
import { useComputeTimerTag } from "./hooks/useComputeTimerTag";
import { isEmpty } from "@swiggy-private/js-utils";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

export interface CouponCardProps {
    type: COUPON_GROUP_TYPES;
    data: CouponInfo;
    onApplyCouponClick: (payload: CouponCardImpressionPayload) => void;
    clickMoreDetailsEvent?: (couponCode: string, couponType: string) => void;
    couponOffersImpressionEvent?: (payload: CouponCardImpressionPayload) => void;
    rewardOfferImpressionEvent?: (payload: CouponCardImpressionPayload) => void;
    isCouponSuggestionFlow?: boolean;
    isPaymentListingCouponCard?: boolean;
    showTncCouponHalfcard?: (coupon: CouponInfo) => void;
    isLastCouponInGroup?: boolean;
    isGiftingCouponListing?: boolean;
    actionType?: ActionType;
    isApplied?: boolean;
    onCheckboxClick?: (couponId: string) => void;
}

const CouponCard: React.FC<CouponCardProps> = ({
    type,
    data,
    onApplyCouponClick,
    clickMoreDetailsEvent,
    isCouponSuggestionFlow,
    couponOffersImpressionEvent,
    rewardOfferImpressionEvent,
    isPaymentListingCouponCard = false,
    showTncCouponHalfcard,
    isLastCouponInGroup = false,
    isGiftingCouponListing,
    actionType,
    isApplied,
    onCheckboxClick,
}: CouponCardProps) => {
    const {
        id,
        title = "",
        applicable = false,
        subtitle,
        description,
        logo,
        ribbonText,
        tnc,
        ctaText,
        emphasisedSubtitle,
        validityText,
        benefitsInfo,
        offerTag,
        couponType,
        ribbonUiMeta,
        tagIdentifiers,
        isSelected,
        businessLine,
    } = data;

    const impressionFiredRef = useRef<boolean>(false);
    const isIPLSwiggySixCouponCard = isIPLSwiggySixesOfferCardWithAsset(tagIdentifiers, ribbonUiMeta);
    const { titleTimerTag, timerAnalyticsPayload } = useComputeTimerTag({ data });

    const applyCouponHandler = useCallback(() => {
        applicable &&
            !isEmpty(title) &&
            onApplyCouponClick({
                offerId: id || "",
                isApplicable: applicable,
                couponCode: title,
                couponType: getCouponType(isPaymentListingCouponCard),
                timerPayload: timerAnalyticsPayload,
                type,
            });
    }, [applicable, title, onApplyCouponClick, id, isPaymentListingCouponCard, timerAnalyticsPayload, type]);

    // Tells if the coupon card is in the payment listing page or restaurant listing page
    const couponCardType = getCouponListingType(isPaymentListingCouponCard);

    // Tells if the coupon card is a payment offer coupon card i.e a couponless offer (Can be present on both restaurant and payment coupon listing page)
    const isPaymentOfferCoupon = isPaymentOfferCouponGroup(type);

    const isBestCoupon = isBestCouponGroup(type) && !isIPLSwiggySixCouponCard;
    const isGreatDealCoupon = isGreatDealCouponGroup(type);
    const isDoubleOffer = isDoubleOfferCouponGroup(couponType);
    const isAppliedCoupon = isAppliedCouponGroup(type);

    const showRibbon = !isPaymentOfferCoupon && ribbonText;

    const onCheckboxClickHandler = useCallback(() => {
        onCheckboxClick?.(id || "");
    }, [onCheckboxClick, id]);

    const onViewEnter = useCallback(() => {
        if (!impressionFiredRef.current) {
            if (isPaymentOfferCoupon) {
                rewardOfferImpressionEvent?.({
                    offerId: id || "",
                    isApplicable: applicable,
                    couponName: title,
                    couponType: getCouponType(isPaymentListingCouponCard),
                });
            } else {
                couponOffersImpressionEvent?.({
                    offerId: id || "",
                    isApplicable: applicable,
                    couponCode: title,
                    couponType: getCouponType(isPaymentListingCouponCard),
                    section: type,
                    timerPayload: timerAnalyticsPayload,
                    description: description,
                    isSelected: isSelected,
                    isApplied: isApplied,
                    applicabletype: businessLine,
                });
            }
            // as ElementObserver is getting retriggered again and again due to timer changes it is causing
            // multiple impressions to fire whenever the timer value changes
            impressionFiredRef.current = true;
        }
    }, [
        applicable,
        couponOffersImpressionEvent,
        description,
        id,
        isPaymentListingCouponCard,
        isPaymentOfferCoupon,
        isApplied,
        isSelected,
        businessLine,
        rewardOfferImpressionEvent,
        timerAnalyticsPayload,
        title,
        type,
    ]);

    const enableTnC = useCallback(() => {
        title && clickMoreDetailsEvent?.(title, type);
        if (isPaymentListingCouponCard) {
            // show tnc for payment listing coupon
            const newData = {
                ...data,
                isCouponLessCard: isPaymentOfferCoupon,
            };
            showTncCouponHalfcard?.(newData);
        }
    }, [
        title,
        type,
        clickMoreDetailsEvent,
        isPaymentListingCouponCard,
        showTncCouponHalfcard,
        data,
        isPaymentOfferCoupon,
    ]);

    const savingsTitleColor: keyof IColors = useMemo(() => {
        if (applicable || isGreatDealCoupon) {
            return "positive";
        }
        if (isGiftingCouponListing) {
            return "negative";
        }
        return "text_Med_Emphasis";
    }, [applicable, isGreatDealCoupon, isGiftingCouponListing]);

    return (
        <ElementObserver onViewEnter={onViewEnter} data-testid={COUPON_CARD_TESTS_IDS.CONTAINER}>
            <Container isLastCouponInGroup={isLastCouponInGroup}>
                <GenericCouponCardComponent
                    timerTag={titleTimerTag}
                    couponName={title}
                    applicable={applicable}
                    savingsTitle={subtitle}
                    savingsTitleColor={savingsTitleColor}
                    savingsSubtitle={emphasisedSubtitle}
                    description={description}
                    logoImageId={logo}
                    ribbonText={ribbonText}
                    ribbonUiMetaData={ribbonUiMeta}
                    showSlantedRects={isBestCoupon}
                    showStarIcon={isDoubleOffer}
                    showApplyCta={!isPaymentOfferCoupon}
                    showRibbon={!!showRibbon}
                    validityText={validityText}
                    offerTag={offerTag}
                    tncData={tnc}
                    benefitsData={benefitsInfo}
                    applyCtaText={ctaText}
                    onApplyCouponClick={applyCouponHandler}
                    moreCtaOnClick={enableTnC}
                    isCouponSuggestionFlow={isCouponSuggestionFlow}
                    couponCardType={couponCardType}
                    isCouponLessCard={isPaymentListingCouponCard && isPaymentOfferCoupon}
                    isV2={isGiftingCouponListing}
                    actionType={actionType}
                    applied={isGiftingCouponListing ? isApplied : isAppliedCoupon}
                    onCheckboxClick={onCheckboxClickHandler}
                />
            </Container>
        </ElementObserver>
    );
};

export default CouponCard;
